// API client for the web surface. Mirrors /app/mobile/src/api.ts (same /api paths, same error parsing).
import { currentLang } from "@/i18n/translate";
const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;
export const BACKEND = BASE;

const TOKEN_KEY = "cw_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export class ApiError extends Error {
  constructor(status, code, message, data) {
    super(message);
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

function parseDetail(status, body) {
  const d = body?.detail;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    return new ApiError(status, d.code || "error", d.message || "Something went wrong.", d);
  }
  if (Array.isArray(d)) {
    const msg = d.map((e) => (typeof e?.msg === "string" ? e.msg.replace(/^Value error, /, "") : "")).filter(Boolean).join(" ");
    return new ApiError(status, "validation", msg || "Please check your input.", d);
  }
  return new ApiError(status, "error", typeof d === "string" ? d : "Something went wrong. Please try again.", body);
}

export async function api(path, opts = {}) {
  const headers = { "Accept-Language": currentLang() };
  // opts.token: a web scoped token (appeal/delete) instead of the admin session.
  const token = opts.token || (opts.auth === false ? null : getToken());
  if (token) headers.Authorization = `Bearer ${token}`;
  let body;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res;
  try {
    res = await fetch(`${API}${path}`, { method: opts.method || (body ? "POST" : "GET"), headers, body });
  } catch {
    throw new ApiError(0, "offline", "You seem to be offline. Check your connection and try again.", null);
  }
  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const e = parseDetail(res.status, json);
    if (res.status === 401 && token && !opts.token) {
      setToken(null);
      window.dispatchEvent(new CustomEvent("cw:unauthorized", { detail: e.message }));
    }
    throw e;
  }
  return json;
}

export const selfieUrl = (path) => `${BASE}${path}?auth=${encodeURIComponent(getToken() || "")}`;

// Signed media paths from the API (/api/media/...) are relative to the backend.
export const mediaUrl = (path) => (path ? (path.startsWith("http") ? path : `${BASE}${path}`) : null);

// Console downloads (CSV, exports) accept the admin token as ?auth=. Empty params are dropped.
export const downloadUrl = (path, params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null && v !== "all"));
  qs.set("auth", getToken() || "");
  return `${API}${path}?${qs}`;
};

// Admin-only media (selfies, report snapshot photos): the backend accepts the admin token as ?auth=.
export const adminMediaUrl = (path) => (path ? `${BASE}${path}${path.includes("?") ? "&" : "?"}auth=${encodeURIComponent(getToken() || "")}` : null);
